import { useState } from "react";
import { CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLoad, Loading, EmptyState, Panel, DataTable, StatusBadge, fmtDate } from "./shared";

const STATUSES = ["pending", "confirmed", "rescheduled", "completed", "no_show", "cancelled"];

const BookingDialog = ({ booking, onClose, onSave }) => {
  const [form, setForm] = useState({
    status: booking.status, confirmed_date: booking.confirmed_date || booking.preferred_date,
    confirmed_time: booking.confirmed_time || booking.preferred_time, admin_notes: booking.admin_notes || "",
  });
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e?.target ? e.target.value : e }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="booking-dialog">
        <DialogHeader><DialogTitle>Manage booking {booking.booking_ref}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-500">{booking.full_name} · {booking.email} · requested {booking.preferred_date} {booking.preferred_time}</p>
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="booking-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Confirmed date</Label><Input type="date" value={form.confirmed_date} onChange={set("confirmed_date")} data-testid="booking-confirmed-date" /></div>
            <div className="space-y-1.5"><Label>Confirmed time</Label><Input type="time" value={form.confirmed_time} onChange={set("confirmed_time")} data-testid="booking-confirmed-time" /></div>
          </div>
          <div className="space-y-1.5"><Label>Internal notes</Label><Textarea value={form.admin_notes} onChange={set("admin_notes")} data-testid="booking-notes" /></div>
          <p className="text-xs text-slate-400">Confirmed, rescheduled or cancelled statuses email the customer automatically.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => onSave(form)} data-testid="booking-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CommerceBookings = ({ call }) => {
  const { data, loading, reload } = useLoad(call, "/bookings");
  const [editing, setEditing] = useState(null);
  if (loading) return <Loading />;
  const rows = data?.bookings || [];
  if (!rows.length) return <EmptyState testid="bookings-empty" icon={CalendarCheck} title="No consultation bookings yet" text="Bookings from /commerce/book-consultation will appear here." />;
  const save = async (form) => {
    await call(`/bookings/${editing.booking_ref}`, { method: "PATCH", body: form });
    toast.success("Booking updated"); setEditing(null); reload();
  };
  return (
    <>
      <Panel testid="commerce-bookings">
        <DataTable testid="bookings-table" rowKey="booking_ref" rows={rows} columns={[
          { key: "booking_ref", label: "Ref", render: r => <span className="font-mono text-xs">{r.booking_ref}</span> },
          { key: "who", label: "Client", render: r => <div><p className="font-medium">{r.full_name}</p><p className="text-xs text-slate-500">{r.email}</p></div> },
          { key: "when", label: "Requested", render: r => `${r.preferred_date} ${r.preferred_time}` },
          { key: "confirmed", label: "Confirmed", render: r => r.confirmed_date ? `${r.confirmed_date} ${r.confirmed_time || ""}` : "—" },
          { key: "service_interest", label: "Service" },
          { key: "status", label: "Status", render: r => <StatusBadge status={r.status} testid={`booking-status-${r.booking_ref}`} /> },
          { key: "created_at", label: "Booked", render: r => fmtDate(r.created_at) },
          { key: "act", label: "", render: r => <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`booking-manage-${r.booking_ref}`}>Manage</Button> },
        ]} />
      </Panel>
      {editing && <BookingDialog booking={editing} onClose={() => setEditing(null)} onSave={save} />}
    </>
  );
};
